import {Injectable}                   from '@angular/core';
import {FileService}                  from '../../services/common/file.service';
import {Base64FileData, FileInfoData} from '../types/ap-file-upload.types';
import {FileType}                     from '../../types/file.types';
import {FileState}                    from '@progress/kendo-angular-upload';

/**
 * Service for managing file upload component operations and file data transformations.
 * Provides utilities for file handling, validation, and state management.
 */
@Injectable({providedIn: 'root'})
export class ApFileUploadComponentService {
  constructor(private fileService: FileService) {
  }

  /**
   * Creates a FileInfoData object from file content and metadata
   * @param id Unique identifier for the file
   * @param uploadDate Optional date when file was uploaded
   * @param content Base64 encoded file content and metadata
   * @param thumbnail Optional base64 encoded thumbnail data
   * @returns FileInfoData object with complete file information
   * @public
   */
  public createFileInfoData(id: string, uploadDate: Date | undefined,
                            content: Base64FileData, thumbnail?: Base64FileData): FileInfoData {
    const fileExtension = content.FileName.split('.').pop();
    const fileInfoData = {
      uid: id,
      name: `${content.FileName}`,
      extension: `.${fileExtension}`,
      size: content.Size,
      MimeType: content.MimeType,
      FileType: this.fileService.getFileType(content.MimeType),
      UploadDate: uploadDate
    } as FileInfoData;
    this.updateRawFileData(fileInfoData, content);
    if (!!thumbnail.MimeType && !!thumbnail.Base64) {
      const thumbnailFileExtension = this._getExtensionFromMimeType(thumbnail.MimeType);
      fileInfoData.ThumbnailFile = this.fileService.base64StringToFileData(`thumbnail.${thumbnailFileExtension}`,
        thumbnail.MimeType, thumbnail.Base64);
    } else {
      fileInfoData.ThumbnailFile = undefined;
    }
    return fileInfoData;
  }

  /**
   * Updates raw file data in FileInfoData object using base64 content
   * @param fileInfoData FileInfoData object to update
   * @param content Base64 encoded file content and metadata
   * @public
   */
  public updateRawFileData(fileInfoData: FileInfoData, content: Base64FileData): void {
    if (!!content.MimeType && !!content.Base64) {
      const file = this.fileService.base64StringToFileData(content.FileName, content.MimeType, content.Base64);
      fileInfoData.rawFile = file;
      fileInfoData.size = file.size;
    }
  }

  /**
   * Checks if file type is either image or video
   * @param fileType FileType to check
   * @returns boolean indicating if file is media type
   * @public
   */
  public isMediaFile(fileType: FileType): boolean {
    return fileType === FileType.Image || fileType === FileType.Video;
  }

  /**
   * Checks if a file already exists in a collection based on name, size, and type
   * @param fileToCompare File to check for duplicates
   * @param compareWith Array of files to compare against
   * @returns boolean indicating if duplicate exists, if true = exist
   * @public
   */
  public isSameFileIsExist(fileToCompare: FileInfoData, compareWith: FileInfoData[]): boolean {
    if (!fileToCompare || !compareWith || compareWith.length <= 0) {
      return false;
    }
    return compareWith.some(x => {
      return fileToCompare.name === x.name
        && fileToCompare.size === x.size
        && fileToCompare.rawFile?.type === x.MimeType;
    });
  }

  /**
   * Checks if file is in Initial or Selected state
   * @param fileInfoData File to check state
   * @returns boolean indicating if file is in Initial or Selected state
   * @public
   */
  public isFileHaveInitOrSelectState(fileInfoData: FileInfoData | undefined): boolean {
    return fileInfoData?.state === FileState.Initial || fileInfoData?.state === FileState.Selected;
  }

  /**
   * Modifies HTTP subscription properties to prevent enumeration issues
   * Makes specific subscription properties non-enumerable to avoid circular reference problems
   * @param files Array of files whose HTTP subscriptions need modification
   * @remarks
   * For cases when need to use JSON.stringify
   * @public
   */
  public modifyHttpSubscriptionProperties(files: FileInfoData[] | undefined): void {
    files?.forEach(file => {
      if (!file.httpSubscription) {
        return;
      }
      const subscription = file.httpSubscription;
      const propsToHandle = ['_parentage', '_teardowns', 'destination'];
      propsToHandle.forEach(prop => {
        const descriptor = Object.getOwnPropertyDescriptor(subscription, prop);
        if (descriptor && descriptor.enumerable !== false) {
          Object.defineProperty(subscription, prop, {
            ...descriptor,
            enumerable: false
          });
        }
      });
    });
  }

  /**
   * Extracts file extension from MIME type
   * @param mimeType MIME type string
   * @returns File extension without leading dot
   * @private
   */
  private _getExtensionFromMimeType(mimeType: string): string {
    return mimeType.split('/')[1];
  }
}
